"use client";

import { useCallback, useRef } from "react";
import { useAuth } from "../providers/AuthProvider";
import { apiFetch } from "../lib/api";
import { useStore } from "../store";
import { useUnreadStatus, useHasAnyUnreads, useLastReadEventId } from "../store/selectors";
import type { Channel } from "../store/types";

export type { UnreadStatus } from "../store/types";

export function useUnreads() {
  const { token } = useAuth();
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const markReadTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const initFromChannels = useCallback((channels: Channel[]) => {
    useStore.getState().initUnreadsFromChannels(channels);
  }, []);

  const handleEvent = useCallback((event: { channelId: string; id: string; eventType: string }) => {
    useStore.getState().onUnreadEvent(event);
  }, []);

  const handleWorkComplete = useCallback((channelId: string) => {
    useStore.getState().onUnreadWorkComplete(channelId);
  }, []);

  const handleReadCursorUpdate = useCallback((channelId: string, userId: string, eventId: string, currentUserId: string | undefined) => {
    useStore.getState().onReadCursorUpdate(channelId, userId, eventId, currentUserId);
  }, []);

  const markRead = useCallback((channelId: string, eventId: string) => {
    // Optimistic update
    useStore.getState().markChannelRead(channelId, eventId);

    // Debounce the API call
    const existing = markReadTimers.current.get(channelId);
    if (existing) clearTimeout(existing);

    markReadTimers.current.set(channelId, setTimeout(async () => {
      markReadTimers.current.delete(channelId);
      const t = tokenRef.current;
      if (!t) return;
      try {
        await apiFetch(`/channels/${channelId}/read-cursor`, t, {
          method: "PUT",
          body: JSON.stringify({ eventId }),
        });
      } catch {
        // ignore
      }
    }, 1500));
  }, []);

  const getStatus = useCallback((channelId: string) => {
    const s = useStore.getState().unreads.byChannel[channelId];
    if (!s || !s.lastEventId) return "none" as const;
    if (s.lastEventId === s.lastReadEventId) return "none" as const;
    if (s.lastEventType === "work" && s.workActive) return "working" as const;
    return "unread" as const;
  }, []);

  const hasAnyUnreads = useCallback(() => {
    const unreads = useStore.getState().unreads.byChannel;
    let hasWorking = false;
    let hasUnread = false;
    for (const u of Object.values(unreads)) {
      if (!u.lastEventId || u.lastEventId === u.lastReadEventId) continue;
      if (u.lastEventType === "work" && u.workActive) {
        hasWorking = true;
      } else {
        hasUnread = true;
      }
    }
    if (hasUnread) return "unread" as const;
    if (hasWorking) return "working" as const;
    return "none" as const;
  }, []);

  const getLastReadEventId = useCallback((channelId: string): string | null => {
    return useStore.getState().unreads.byChannel[channelId]?.lastReadEventId ?? null;
  }, []);

  return {
    initFromChannels,
    handleEvent,
    handleWorkComplete,
    handleReadCursorUpdate,
    markRead,
    getStatus,
    hasAnyUnreads,
    getLastReadEventId,
  };
}
